import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const NAV = [
  { path: "/",            label: "Overview",    icon: "⬡" },
  { path: "/air-quality", label: "Air Quality", icon: "◈" },
  { path: "/weather",     label: "Weather",     icon: "◇" },
  { path: "/satellites", label: "Satellites", icon: "🛰️" },
  { path: "/map",         label: "GIS Map",     icon: "◉" },
  { path: "/alerts",      label: "Alerts",      icon: "△" },
  { path: "/reports",     label: "Reports",     icon: "▦" },
  { path: "/ai",          label: "AI Intel",    icon: "✦", pro: true },
];

const TIER_COLORS = {
  free:       "#4a5568",
  basic:      "#3b82f6",
  pro:        "#22c55e",
  enterprise: "#a855f7",
};

const s = {
  root: { display: "flex", height: "100vh", overflow: "hidden" },
  sidebar: {
    width: 210, minWidth: 210, flexShrink: 0,
    background: "var(--surface)",
    borderRight: "1px solid var(--border)",
    display: "flex", flexDirection: "column",
    padding: "20px 0",
  },
  logo: { padding: "0 16px 24px", borderBottom: "1px solid var(--border)", marginBottom: 8 },
  logoTitle: { fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, color: "var(--accent)", letterSpacing: "0.14em" },
  logoSub: { fontSize: 9, color: "var(--subtext)", letterSpacing: "0.12em", marginTop: 2 },
  navItem: {
    display: "flex", alignItems: "center", gap: 9,
    padding: "8px 16px", fontSize: 12, fontWeight: 600,
    color: "var(--subtext)", transition: "all 0.15s",
    borderLeft: "2px solid transparent", cursor: "pointer",
  },
  navIcon: { fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 },
  main: { flex: 1, overflow: "auto", padding: "24px 28px" },
  topbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 24,
  },
  liveChip: {
    display: "flex", alignItems: "center", gap: 7,
    fontSize: 10, color: "var(--subtext)", fontFamily: "var(--mono)",
  },
  userChip: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "5px 12px", borderRadius: 7,
    background: "var(--surface2)", border: "1px solid var(--border2)",
    fontSize: 11, cursor: "pointer",
  },
  footer: {
    marginTop: "auto", padding: "16px 16px 0",
    borderTop: "1px solid var(--border)",
    fontSize: 9, color: "var(--subtext)", fontFamily: "var(--mono)",
  },
};

const activeStyle = {
  color: "var(--text)",
  borderLeftColor: "var(--accent)",
  background: "rgba(0,229,170,0.05)",
};

const proActiveStyle = {
  color: "var(--accent)",
  borderLeftColor: "var(--accent)",
  background: "rgba(0,229,170,0.05)",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const now = new Date().toUTCString().slice(5, 25);
  const tier = user?.tier || "free";
  const tierColor = TIER_COLORS[tier] || TIER_COLORS.free;

console.log("Current API Target:", "https://your-new-nightingale-core.onrender.com");
  
  return (
    <div style={s.root}>
      <aside style={s.sidebar}>
        <div style={s.logo}>
          <div style={s.logoTitle}>HIGH HORSE</div>
          <div style={s.logoSub}>ENVIRONMENTAL INTEL</div>
        </div>

        <nav>
          {NAV.map(({ path, label, icon, pro }) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              style={({ isActive }) => ({
                ...s.navItem,
                ...(isActive ? (pro ? proActiveStyle : activeStyle) : {}),
              })}
            >
              <span style={{ ...s.navIcon, color: pro ? "var(--accent)" : "inherit" }}>{icon}</span>
              {label}
              {pro && (
                <span style={{
                  marginLeft: "auto", fontSize: 8, fontFamily: "var(--mono)",
                  color: "var(--accent)", letterSpacing: "0.1em",
                }}>PRO</span>
              )}
            </NavLink>
          ))}
        </nav>

        {tier === "free" && (
          <div style={{
            margin: "16px 12px", padding: "12px",
            background: "rgba(0,229,170,0.06)", border: "1px solid rgba(0,229,170,0.15)",
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--accent)", marginBottom: 4 }}>
              FREE PLAN
            </div>
            <div style={{ fontSize: 10, color: "var(--subtext)", lineHeight: 1.6, marginBottom: 10 }}>
              Unlock AI features, all regions & API access
            </div>
            <button className="btn btn-primary" onClick={() => navigate("/account")}
              style={{ width: "100%", justifyContent: "center", fontSize: 10, padding: "6px 12px" }}>
              Upgrade
            </button>
          </div>
        )}

        <div style={s.footer}>
          <div>v2.0.0 · High Horse Intel</div>
          <div style={{ marginTop: 2 }}>{now} UTC</div>
        </div>
      </aside>

      <main style={s.main}>
        <div style={s.topbar}>
          <div style={s.liveChip}>
            <span className="pulse" />
            LIVE · {now}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!user ? (
              <>
                <button className="btn btn-outline" onClick={() => navigate("/login")} style={{ fontSize: 11, padding: "5px 14px" }}>Sign in</button>
                <button className="btn btn-primary" onClick={() => navigate("/register")} style={{ fontSize: 11, padding: "5px 14px" }}>Sign up free</button>
              </>
            ) : (
              <div style={s.userChip} onClick={() => navigate("/account")}>
                <span style={{
                  background: tierColor + "20",
                  border: `1px solid ${tierColor}60`,
                  color: tierColor,
                  padding: "2px 8px",
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  fontFamily: "var(--mono)",
                  fontWeight: 700,
                }}>
                  {tier.toUpperCase()}
                </span>
                <span style={{ fontSize: 11, color: "var(--text)" }}>{user.full_name?.split(" ")[0] || user.email?.split("@")[0]}</span>
                <span style={{ fontSize: 10, color: "var(--subtext)" }}
                  onClick={(e) => { e.stopPropagation(); logout(); navigate("/"); }}>
                  · sign out
                </span>
              </div>
            )}
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}
